import type * as React from "react";

import { cn } from "@/lib/utils";

// ===========================================================================
// `shrink-0` NO ES DECORACIÓN, y por eso va comentado.
//
// La Card es `overflow-hidden` —lo necesita para que el contenido respete las
// esquinas redondeadas— y vive dentro de `#contenido`, que es un `flex-col` de
// altura acotada (`_app.tsx:43-51`, con `h-svh overflow-hidden` encima). Sin
// `shrink-0`, la Card es un hijo flexible: se encoge por debajo de su
// contenido y, al recortar, corta lo que sobra en vez de dejar crecer la
// página. El contenedor entonces cree que todo cabe y no aparece ningún
// scroll.
//
// Medido en `/movimientos` antes de esto, con la ventana a 1.025 px:
//
//   Card        scrollHeight 1450  clientHeight  616   ← recortaba 834 px
//   #contenido  scrollHeight  969  clientHeight  969   ← creía que cabía
//   filas en el DOM 25 · alcanzables 10 · INALCANZABLES 15
//
// De las 25 filas que el servidor manda por página se podían tocar 10. No es
// que el scroll fuera mal: no había scroll. Con `shrink-0` la Card conserva su
// altura natural, `#contenido` crece por encima de la suya y vuelve a
// desplazarse, que es como se comporta el resto de la aplicación.
//
// Afecta a las seis pantallas que usan Card con contenido alto —movimientos,
// cierre, empresas, tipo de cambio, auditoría y usuarios—, y todas estaban
// expuestas al mismo recorte.
// ===========================================================================

function Card({
  className,
  size = "default",
  ...props
}: React.ComponentProps<"div"> & { size?: "default" | "sm" }) {
  return (
    <div
      data-slot="card"
      data-size={size}
      className={cn(
        "group/card flex shrink-0 flex-col gap-4 overflow-hidden rounded-xl bg-card py-4 text-card-foreground text-xs/relaxed ring-1 ring-foreground/10 has-[>img:first-child]:pt-0 has-data-[slot=card-footer]:pb-0 data-[size=sm]:gap-2 data-[size=sm]:py-3 data-[size=sm]:has-data-[slot=card-footer]:pb-0",
        className,
      )}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "group/card-header @container/card-header grid auto-rows-min items-start gap-1 px-4 has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto] group-data-[size=sm]/card:px-3 [.border-b]:pb-4 group-data-[size=sm]/card:[.border-b]:pb-3",
        className,
      )}
      {...props}
    />
  );
}

function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        "font-medium text-sm group-data-[size=sm]/card:text-sm",
        className,
      )}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground text-xs/relaxed", className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className,
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-4 group-data-[size=sm]/card:px-3", className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        "flex items-center border-t p-4 group-data-[size=sm]/card:p-3",
        className,
      )}
      {...props}
    />
  );
}

export {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
};
